import { MRT_ColumnDef } from "material-react-table";
import { useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom'
import {
  GeneralTable,
  SingleTableBoxScene,
  CustomSearch
} from "@/shared/components/ui";
import { PermissionsEnum } from '@/auth/interfaces';
import { useUiConfirmModalStore } from "@/store/ui";
import {
  useTableFilter,
  useTableServerSideFiltering,
  useCheckPermissions,
  hasPermission,
  formatDateWithTime
} from "@/shared";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { SolicitudCorporativa } from "@/shared/interfaces/erp/corporativa/solicitudCorporativa.interface"
import { useFetchgetSolicitudesTotales } from '@/shared/actions/erp/solicitudpreventacoorporativo/solicitur-preventa-corporativo.actions'
import ModalComentariosSolicitud from '@/erp/operaciones/solicitud-preventa/shared/ModalComentariosSolicitud';
import { getEstadoConfig, getComplejidadConfig, chipStyle } from '@/erp/operaciones/solicitud-preventa/shared/estado-solicitud';
import { IconButton, Tooltip } from "@mui/material";

const VerificacionSolicitudPreventa: React.FC = () => {
  useCheckPermissions(PermissionsEnum.api_view_solicitudcorporativa);
  const {
    globalFilter,
    pagination,
    onChangeFilter,
    setPagination,
    cleanFilter,
    searchTerm,
  } = useTableFilter();
  //modal
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen
  );
  //ocupar para filtrar las columnas
  const { columnFilters, setColumnFilters } = useTableServerSideFiltering();
  //paginacion
  const { pageIndex, pageSize } = pagination;
  let { data: rawSolicitudes, isLoading, isFetching } = useFetchgetSolicitudesTotales({
    params: { page: pageIndex + 1, page_size: pageSize, search: searchTerm, ...columnFilters }
  });
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState<string | undefined>(undefined);

  // Solo se gestiona cuando está EN ESPERA; ya gestionada (revisada/rechazada) es solo lectura.
  const esGestionable = (estado?: string) => (estado ?? '').toUpperCase() === 'ESPERA';

  const onEdit = (original: SolicitudCorporativa) => {
    if (!esGestionable(original.estado_solicitud)) {
      setSolicitudSeleccionada(original.id_solicitud_corporatavia);
      setOpenModal(true);
    } else {
      setConfirmDialog({
        isOpen: true,
        title: "Gestionar Solicitud",
        subtitle: "¿Está seguro que desea gestionar este registro?",
        onConfirm: () => {
          setConfirmDialogIsOpen(false);
          navigate(`/solicitud/crear-comentario/${original.id_solicitud_corporatavia}`);
        },
      });
    }
  };

  // Mapear los datos del backend al formato que espera la tabla
  const solicitudes: SolicitudCorporativa[] = useMemo(() => {
    if (!rawSolicitudes?.data?.data) return [];

    return rawSolicitudes.data.data.map(s => {
      const numeroSucursales = s.numero_sucursales ??
        (s.clientes?.reduce((acc, c) => acc + (c.sucursales?.length || 0), 0) ?? 0);

      const nombreCliente = (s.clientes && s.clientes.length > 0)
        ? s.clientes.map(c => c.cliente_oficina).filter(Boolean).join(", ")
        : "Sin cliente";

      return {
        id_solicitud_corporatavia: s.id_solicitud_corporatavia,
        codigo: s.codigo ?? "—",
        user_name: s.user_name,
        nombre_cliente: nombreCliente,
        comentario_general: s.comentario_general ?? "",
        estado_solicitud: s.estado_solicitud,
        complejidad: s.complejidad ?? "",
        user_gestiona_name: s.user_gestiona_name ?? "",
        fecha_creacion: s.fecha_creacion ?? "",
        fecha_gestion: s.fecha_gestion ?? "",
        numero_sucursales: numeroSucursales,
      } as SolicitudCorporativa;
    });
  }, [rawSolicitudes]);

  const columns = useMemo<MRT_ColumnDef<SolicitudCorporativa>[]>(() => [
    {
      accessorKey: "codigo",
      header: "ID Único",
      size: 140,
      enableColumnFilter: false,
      Cell: ({ cell }) => (
        <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>
          {cell.getValue<string>() || "—"}
        </span>
      ),
    },
    {
      accessorKey: "nombre_cliente",
      header: "Cliente",
      size: 180,
      enableColumnFilter: false,
      Cell: ({ cell }) => cell.getValue<string>() || "—",
    },
    {
      accessorKey: "user_name",
      header: "Usuario Creó",
      size: 150,
      enableColumnFilter: false,
      Cell: ({ cell }) => cell.getValue<string>() || "—",
    },
    {
      accessorKey: "numero_sucursales",
      header: "N° Sucursales",
      size: 120,
      enableColumnFilter: false,
      Cell: ({ cell }) => (
        <span style={{ fontWeight: 600 }}>{cell.getValue<number>() ?? 0}</span>
      ),
    },
    {
      accessorKey: "estado_solicitud",
      header: "Estado",
      size: 150,
      Cell: ({ cell }) => {
        const cfg = getEstadoConfig(cell.getValue<string>());
        return <span style={chipStyle(cfg)}>{cfg.label.toUpperCase()}</span>;
      },
    },
    {
      accessorKey: "complejidad",
      header: "Complejidad",
      size: 130,
      enableColumnFilter: false,
      Cell: ({ cell }) => {
        const cfg = getComplejidadConfig(cell.getValue<string>());
        return cfg
          ? <span style={chipStyle(cfg)}>{cfg.label.toUpperCase()}</span>
          : <span style={{ color: '#9e9e9e' }}>—</span>;
      },
    },
    {
      accessorKey: "user_gestiona_name",
      header: "Usuario Gestionó",
      size: 150,
      enableColumnFilter: false,
      Cell: ({ cell }) => cell.getValue<string>() || "—",
    },
    {
      accessorKey: "fecha_creacion",
      header: "Fecha Creación",
      size: 160,
      enableColumnFilter: false,
      Cell: ({ cell }) =>
        cell.getValue<string>() ? formatDateWithTime(cell.getValue<string>()) : "—",
    },
    {
      accessorKey: "fecha_gestion",
      header: "Fecha Gestión",
      size: 160,
      enableColumnFilter: false,
      Cell: ({ cell }) =>
        cell.getValue<string>() ? formatDateWithTime(cell.getValue<string>()) : "—",
    },
  ], []);

  return (
    <SingleTableBoxScene
      title="Gestión de Solicitudes Corporativas"
      enableRedirect={false}
    >
      <CustomSearch
        onChange={(value) => {
          onChangeFilter(value);   // actualiza searchTerm
          setPagination(prev => ({ ...prev, pageIndex: 0 }));
        }}
        value={globalFilter}
        text="Filtrar por cliente o estado de la solicitud"
        cleanFilter={cleanFilter}
      />

      <GeneralTable<SolicitudCorporativa>
        columns={columns}
        data={solicitudes}
        isLoading={isLoading}
        isRefetching={isFetching}
        enableGlobalFilter={false}
        enableColumnFilters={true}
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        pagination={pagination}
        onPaging={setPagination}
        rowCount={rawSolicitudes?.data.count ?? 0}
        positionActionsColumn="first"
        canEdit={hasPermission(PermissionsEnum.api_change_solicitudcorporativa)}
        onEdit={onEdit}
        editIconColor={"info"}
        toolTipTitleEditIcon="Gestionar registro"
        onCustomEdit={(solicitud) => {
          const gestionable = esGestionable(solicitud.estado_solicitud);
          return (
            <Tooltip title={gestionable ? "Gestionar registro" : "Ver detalles"}>
              <IconButton color="info" onClick={() => onEdit(solicitud)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          );
        }}
      />
      <ModalComentariosSolicitud
        open={openModal}
        setOpen={setOpenModal}
        solicitud={solicitudSeleccionada}
      />
    </SingleTableBoxScene>
  );
};

export default VerificacionSolicitudPreventa;
